// apps/accounts/src/components/Sidebar.jsx
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  FolderOpen,
  Users,
  UserCheck,
  User,
  Calendar,
  Package,
  Tag,
  Ruler,
  Truck,
  ClipboardList,
  FileText,
  DollarSign,
  CreditCard,
  TrendingUp,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Building2,
  UserCog,
  Shield,
  Eye,
  Settings,
  LogOut,
  Sparkles,
  ClipboardCheck,
  UserMinus,
  Wallet,
  Coins,
  Receipt,
  Calculator,
  Layers,
  FileSpreadsheet,
  FileCheck, // ? Labour Bills ????
} from 'lucide-react';
import { cn } from '../lib/utils';

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const role = user?.role || 'user';
  
  // State for dropdowns
  const [expensesOpen, setExpensesOpen] = useState(true);
  const [materialsOpen, setMaterialsOpen] = useState(true);
  const [partnerManagementOpen, setPartnerManagementOpen] = useState(true);
  const [staffManagementOpen, setStaffManagementOpen] = useState(true);
  const [measurementsOpen, setMeasurementsOpen] = useState(true);

  const isPartner = role === 'partner-construction' || role === 'partner-interior' || role === 'client';

  // --- Menu Groups --------------------------------------------------
  const mainItems = [
    { path: '/projects', label: 'Projects', icon: FolderOpen },
    { path: '/partners', label: 'Partners', icon: Users },
    { path: '/vendors', label: 'Vendors', icon: UserCheck },
    { path: '/labour', label: 'Labour', icon: User },
    { path: '/attendance', label: 'Attendance', icon: Calendar },
  ];

  const partnerItems = [
    { path: '/partner-site-management', label: 'Partner Management', icon: Shield },
    { path: '/site-employees', label: 'Site Employees', icon: UserCog },
  ];

  // ? Labour Bills ??? ??? ????? ?????
  const expenseItems = [
    { path: '/expenses', label: 'Project Expenses', icon: DollarSign },
    { path: '/vendor-payments', label: 'Vendor Payments', icon: CreditCard },
    { path: '/labour-bills', label: 'Labour Bills', icon: FileCheck }, // ? ????
  ];

  const materialsItems = [
    { path: '/materials', label: 'Materials List', icon: Package },
    { path: '/materials/categories', label: 'Categories', icon: Tag },
    { path: '/materials/uoms', label: 'UOMs', icon: Ruler },
    { path: '/materials/suppliers', label: 'Suppliers', icon: Truck },
    { path: '/materials/requests', label: 'Requests', icon: ClipboardList },
    { path: '/materials/purchase-orders', label: 'Purchase Orders', icon: FileText },
  ];

  // --- Measurement & Bill Management Items ------------------------
  const measurementItems = [
    { path: '/measurements/categories', label: 'Work Categories', icon: Layers },
    { path: '/measurements/rates', label: 'Project & Vendor Rates', icon: Coins },
    { path: '/measurements', label: 'Site Measurements', icon: Calculator },
    { path: '/measurements/bills', label: 'Site Bills', icon: FileSpreadsheet },
  ];

  // --- Staff Management Items -----------------------------------------
  const staffItems = [
    { path: '/staff/attendance', label: 'Attendance', icon: ClipboardCheck },
    { path: '/staff/leave', label: 'Leave Management', icon: UserMinus },
    { path: '/staff/salary', label: 'Salary', icon: Wallet },
    { path: '/staff/advance', label: 'Advance Payments', icon: Coins },
  ];

  const otherItems = [
    { path: '/client-payments', label: 'Client Payments', icon: TrendingUp },
    { path: '/investments', label: 'Investments', icon: CreditCard },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  const renderNavLink = (item) => {
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    
    return (
      <NavLink
        key={item.path}
        to={item.path}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
          isActive
            ? 'bg-amber-500/10 text-amber-400 border-r-2 border-amber-400'
            : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
        )}
      >
        <item.icon className="w-5 h-5 flex-shrink-0" />
        <span className="truncate">{item.label}</span>
      </NavLink>
    );
  };

  const renderGroup = (title, icon, items, open, setOpen) => {
    if (!items || items.length === 0) return null;
    const Icon = icon;
    const isGroupActive = items.some(item => 
      location.pathname === item.path || location.pathname.startsWith(item.path + '/')
    );
    
    return (
      <div className="mt-2">
        <button
          onClick={() => setOpen(!open)}
          className={cn(
            'flex items-center justify-between w-full px-3 py-2 text-sm font-medium transition rounded-lg hover:bg-slate-800/50',
            isGroupActive ? 'text-amber-400' : 'text-slate-400 hover:text-white'
          )}
        >
          <span className="flex items-center gap-3">
            <Icon className="w-5 h-5 flex-shrink-0" />
            <span>{title}</span>
          </span>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {open && (
          <div className="ml-6 space-y-1 mt-1 border-l border-slate-700/50 pl-2">
            {items.map(renderNavLink)}
          </div>
        )}
      </div>
    );
  };

  // --- Partner Sidebar --------------------------------------------
  if (isPartner) {
    return (
      <aside className="w-64 bg-slate-900 text-slate-300 h-full overflow-y-auto flex-shrink-0 border-r border-slate-800">
        <div className="p-4 border-b border-slate-800/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">
                {role === 'partner-construction' ? 'Construction' : role === 'partner-interior' ? 'Interior' : 'Client'}
              </p>
              <p className="text-xs text-slate-400">{user?.name || 'Partner'}</p>
            </div>
          </div>
        </div>
        <nav className="p-3 space-y-1">
          {renderNavLink({ path: '/dashboard/main', label: 'Dashboard', icon: LayoutDashboard })}
          {renderNavLink({ path: '/partner-sites', label: 'My Sites', icon: Building2 })}
          
          <div className="mt-4 pt-4 border-t border-slate-700/50">
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400">
              <Eye className="w-4 h-4 flex-shrink-0" />
              <span>Read-only access</span>
            </div>
          </div>
          
          <div className="mt-2 pt-2 border-t border-slate-700/50">
            <button
              onClick={logout}
              className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              <span>Logout</span>
            </button>
          </div>
        </nav>
      </aside>
    );
  }

  // --- Admin Sidebar ----------------------------------------------
  return (
    <aside className="w-64 bg-slate-900 text-slate-300 h-full overflow-y-auto flex-shrink-0 border-r border-slate-800">
      <div className="p-4 border-b border-slate-800/50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Shield className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Management</p>
            <p className="text-xs text-slate-400">{user?.name || 'Admin'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-800/50">
          <span className="text-xs text-amber-400">?? Full Access</span>
        </div>
      </div>

      <nav className="p-3 space-y-1">
        {renderNavLink({ path: '/dashboard/main', label: 'Dashboard', icon: LayoutDashboard })}
        {mainItems.map(renderNavLink)}
        
        {renderGroup('Partner Management', Shield, partnerItems, partnerManagementOpen, setPartnerManagementOpen)}
        
        {renderGroup('Staff Management', UserCog, staffItems, staffManagementOpen, setStaffManagementOpen)}
        
        {renderGroup('Measurements & Billing', Receipt, measurementItems, measurementsOpen, setMeasurementsOpen)}
        
        {/* Expenses Group � Labour Bills ????? ????? */}
        {renderGroup('Expenses', DollarSign, expenseItems, expensesOpen, setExpensesOpen)}
        
        {renderGroup('Materials', Package, materialsItems, materialsOpen, setMaterialsOpen)}
        
        {otherItems.map(renderNavLink)}
        
        <div className="mt-4 pt-4 border-t border-slate-700/50">
          <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400">
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            <span>{user?.name || user?.email || 'User'}</span>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span>Logout</span>
          </button>
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;